import type {DiffAnchor} from '@github-ui/diffs/types'
import {useAnalytics} from '@github-ui/use-analytics'
import {type FocusEvent, type RefObject, useMemo, useRef} from 'react'

import type {ConfigureSuggestedChangesImplementation} from '../../conversation-types'
import type {StartThreadCommentProps} from './StartThreadComment'
import {StartThreadComment} from './StartThreadComment'
import {useGetSelectedLineRange} from '../../stores/selectors'

export interface StartConversationProps
  extends Pick<
    StartThreadCommentProps,
    | 'addCommentDialogTitle'
    | 'batchingEnabled'
    | 'fileLevelComment'
    | 'filePath'
    | 'focusOnMount'
    | 'isLeftSide'
    | 'lineNumber'
    | 'onAddComment'
    | 'startLineNumber'
    | 'subjectId'
    | 'viewerData'
    | 'threadsConnectionId'
  > {
  anchorRef: RefObject<HTMLElement>
  diffAnchor: DiffAnchor
  isInDialogMode: boolean
  isOpen: boolean
  onCloseCommentDialog: (shouldFocusCell?: boolean) => void
  returnFocusRef: RefObject<HTMLElement>
  suggestedChangesConfig?: ConfigureSuggestedChangesImplementation
}

/**
 * Renders a modal AnchoredOverlay for starting a new conversation thread or an in line version
 */
export function StartConversation({
  anchorRef,
  diffAnchor,
  isInDialogMode,
  isOpen,
  returnFocusRef,
  onCloseCommentDialog,
  suggestedChangesConfig,
  ...rest
}: StartConversationProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const newCommentContainerRef = useRef<HTMLDivElement>(null)
  const getSelectedLineRange = useGetSelectedLineRange()

  const config = useMemo(() => {
    if (isOpen && suggestedChangesConfig?.configureSuggestedChangesFromLineRange) {
      const selectedLineRange = getSelectedLineRange()
      return suggestedChangesConfig.configureSuggestedChangesFromLineRange(
        selectedLineRange,
        suggestedChangesConfig?.shouldStartNewConversationWithSuggestedChange,
      )
    }
    return undefined
  }, [isOpen, suggestedChangesConfig, getSelectedLineRange])

  if (!isOpen) return null

  const handleNewCommentContainerFocus = (e: FocusEvent) => {
    // Only handle focus logic if a user is on the new comment container's first <div> element.
    if (e.target !== newCommentContainerRef.current) return

    // Focus on the textarea element inside of the new comment container
    newCommentContainerRef.current.querySelector('textarea')?.focus()
  }

  return (
    <div
      className="rounded-2 bgColor-default"
      data-marker-navigation-new-thread
      data-marker-id="new-comment" // Add data-marker-id attribute for keyboard navigation
      ref={newCommentContainerRef}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isInDialogMode ? 0 : -1}
      onFocus={handleNewCommentContainerFocus}
    >
      <StartThreadComment
        onClose={(shouldFocusCell?: boolean) => {
          onCloseCommentDialog(shouldFocusCell)
          sendAnalyticsEvent('comments.cancel_thread_reply', 'CANCEL_REVIEW_THREAD_BUTTON')
        }}
        showOnCloseIcon={false}
        suggestedChangesConfig={config}
        {...rest}
      />
    </div>
  )
}

try{ StartConversation.displayName ||= 'StartConversation' } catch {}